import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { connect } from 'react-redux';

import i18n from '../../i18n';
import Conversation from '../../models/Conversation';
import Exchange from '../../models/Exchange';
import { NumberFormatter } from '../../utils/Intl';
import EditableField from '../widget/EditableField';
import Tooltip from '../widget/Tooltip';

import GuestpointsPurchaseRow from './GuestpointsPurchaseRow';
import PromoGPInfoLabel from './PromoGPInfoLabel';
import EditGuestPointsInput from './EditGuestPointsInput';

export class GuestpointsField extends React.PureComponent {
    static propTypes = {
        conversation: PropTypes.instanceOf(Conversation).isRequired,
        exchange: PropTypes.instanceOf(Exchange).isRequired,
        isGuest: PropTypes.bool,
        handleChange: PropTypes.func.isRequired,
        currency: PropTypes.string,
        coupon: PropTypes.shape({
            couponType: PropTypes.string,
            couponName: PropTypes.string,
            discount: PropTypes.number
        }),
        estimatedMaxGPPerNight: PropTypes.number,
        isOnModification: PropTypes.bool
    };

    constructor(props) {
        super(props);
        this.guestpointsRenderer = this.guestpointsRenderer.bind(this);
        this.guestpointsExtraRenderer = this.guestpointsExtraRenderer.bind(this);
        this.getInputValue = this.getInputValue.bind(this);
    }

    guestpointsRenderer(value) {
        const { exchange, isGuest } = this.props;

        if (this.props.conversation.doesInterlocutorWantReciprocalWithoutGP()) {
            value = 0;
        }
        const detail = i18n.t('common:value_per_night', {
            value: `${NumberFormatter.format(value)} GP`,
            nb_night: this.props.exchange.getNightNb()
        });
        const sign = isGuest ? '-' : '+';
        // ===== to prevent undefined value (need deep review .......) =======
        const totalGp = exchange.getGpTotal(value) || 0;
        return `${detail}<span class="pull-right">${sign}${NumberFormatter.format(totalGp)} GP</span>`;
    }

    guestpointsExtraRenderer() {
        const { conversation, exchange, isGuest, currency } = this.props;
        const gpEarned = conversation.getGPEarned();
        if (isGuest) {
            return gpEarned && !exchange.get('gp_balance') ? (
                <div className="extra">
                    <label>
                        {i18n.t('exchange:gp_earned')}
                        <Tooltip
                            title={i18n.t('exchange:gp_given_by', {
                                first_name: exchange.get('host').get('first_name')
                            })}
                        />
                    </label>
                    <span className="value">+{NumberFormatter.format(gpEarned)} GP</span>
                </div>
            ) : (
                <GuestpointsPurchaseRow exchange={exchange} currency={currency} />
            );
        }
    }

    shouldResetAmount() {
        const { conversation, exchange } = this.props;
        return conversation.hasOneOrMoreGPFree() && exchange.get('guestpoint_amount') === 0;
    }

    getInputValue() {
        const { exchange } = this.props;
        return this.shouldResetAmount() ? exchange.get('home_guestpoint') : exchange.get('guestpoint_amount');
    }

    render() {
        const { isGuest, handleChange, conversation, estimatedMaxGPPerNight, isOnModification } = this.props;
        return (
            <>
                <EditableField
                    editable={!isGuest && !isOnModification}
                    name="guestpoint_amount"
                    label={i18n.t('exchange:guestpoints')}
                    value={this.getInputValue()}
                    renderer={this.guestpointsRenderer}
                    extra={this.guestpointsExtraRenderer}
                    onChange={handleChange}
                    inputValueTransformer={this.inputValueTransformer}
                >
                    <EditGuestPointsInput sliderMax={estimatedMaxGPPerNight} />
                </EditableField>
                {conversation.exchangeStartsInGivenDateRange(
                    moment(Exchange.GP_PROMO_START_DATE),
                    Exchange.GP_PROMO_NB_DAYS
                ) &&
                    this.props.coupon &&
                    this.props.coupon.couponType.toLowerCase() === 'guestpoint' && (
                        <PromoGPInfoLabel amount={this.props.coupon.discount} />
                    )}
            </>
        );
    }
}

export const mapStateToProps = (state, ownProps) => {
    const homeId = parseInt(ownProps.conversation.getExchangeAsHost()?.get('home')?.get('id'), 10);
    const userHomes = state.homes.length > 0 ? state.homes : null;
    const currentHome = userHomes?.find((h) => {
        return h.id === homeId;
    });
    return {
        ...(currentHome && {
            estimatedMaxGPPerNight: currentHome.get('max_guestpoints_per_night')
        })
    };
};

export default connect(mapStateToProps)(GuestpointsField);
